import { AxiosResponse } from "axios";
import Attachment from "../../components/Gallery/model/Attachment.model";
import ApiResponse from "../models/ApiResponse";
import TestSeries from "../models/test-series/TestSeries";
import TestSeriesDTO from "../models/TestSeriesDTO";
import Api from "./Api";

export type ResponseType = "arraybuffer" | "blob" | "document" | "json" | "text" | "stream";

export type TestSeriesAttachmentReference = Required<TestRequestRaw.Schemas.TestSeriesAttachmentReferenceDTO>;
class TestSeriesApi {
  constructor(private api: Api) {}

  public fetchTestSeriesById(testSeriesId: number): Promise<AxiosResponse<TestSeries>> {
    return this.api.get(`/test-request/test-series/${testSeriesId}`);
  }

  public fetchTestSeriesVersionById(testSeriesId: number, testSeriesVersion: number): ApiResponse<TestSeriesDTO> {
    return this.api.get(`/test-request/test-series/${testSeriesId}/version/${testSeriesVersion}`);
  }

  public exportAllTestPlansOfTestRequest(testRequestId: string): Promise<AxiosResponse<BlobPart>> {
    const arraybuffer: ResponseType = "arraybuffer";
    const additionalConfig = {
      responseType: arraybuffer,
    };
    return this.api.get(`/test-request/test-plans/${testRequestId}/exportAll`, additionalConfig);
  }

  public exportAllMatrixBasedTestPlansOfTestRequest(testRequestId: string): Promise<AxiosResponse<BlobPart>> {
    const arraybuffer: ResponseType = "arraybuffer";
    const additionalConfig = {
      responseType: arraybuffer,
    };
    return this.api.get(`/test-request/test-plans/${testRequestId}/matrixbasedexportall`, additionalConfig);
  }

  public exportAllMergedTestPlansOfTestRequest(testRequestId: string): Promise<AxiosResponse<BlobPart>> {
    const arraybuffer: ResponseType = "arraybuffer";
    const additionalConfig = {
      responseType: arraybuffer,
    };
    return this.api.get(`/test-request/test-plans/${testRequestId}/mergedexportall`, additionalConfig);
  }

  public duplicateTestSeriesById(id: number): Promise<AxiosResponse> {
    return this.api.post(`/test-request/test-series/${id}/duplicate`, {});
  }

  public deleteTestSeriesById(id: number): Promise<AxiosResponse<boolean>> {
    return this.api.delete(`/test-request/test-series/${id}`);
  }

  public cancelTestSeriesById(id: number): Promise<AxiosResponse<boolean>> {
    return this.api.put(`/test-request/test-series/${id}/cancel`);
  }

  public reopenTestSeriesById(id: number): Promise<AxiosResponse<TestSeriesDTO>> {
    return this.api.put(`/test-request/test-series/${id}/reopen`);
  }

  public moveTestSeriesToDraft(id: number): Promise<AxiosResponse<TestSeriesDTO>> {
    return this.api.put(`/test-request/test-series/${id}/move-to-draft`);
  }

  public fetchAttachmentsToTestSeries(
    testSeriesId: number,
    version: number,
  ): Promise<AxiosResponse<TestSeriesAttachmentReference[]>> {
    return this.api.get(`/test-request/v2/test-series/${testSeriesId}/version/${version}/attachments`);
  }

  public removeAttachmentFromTestSeries(testSeriesId: number, uuids: string[]): Promise<Attachment[]> {
    return this.api
      .deleteByPayload(`/test-request/v2/test-series/${testSeriesId}/attachments`, uuids)
      .then((response: AxiosResponse<Required<TestRequestRaw.Schemas.TestSeriesAttachmentReferenceDTO>[]>) =>
        response.data.map(({ uuid, editable }) => ({
          uuid,
          editable,
        })),
      );
  }

  public addAttachmentsToTestCase(
    testSeriesId: number,
    payload: TestRequestRaw.Schemas.CreateTestCaseAttachments,
  ): Promise<AxiosResponse> {
    return this.api.post(`/test-request/v2/test-series/${testSeriesId}/attachments`, payload);
  }

  public fetchAttachmentsToTestCase(testCaseId: number, testSeriesId: number): Promise<AxiosResponse<string[]>> {
    return this.api.get(`/test-request/v2/test-series/${testSeriesId}/test-cases/${testCaseId}/attachments`);
  }

  public removeAttachmentFromTestCase(
    testCaseId: number,
    testSeriesId: number,
    payload: string[],
  ): Promise<AxiosResponse<string[]>> {
    return this.api.deleteByPayload(
      `/test-request/v2/test-series/${testSeriesId}/test-cases/${testCaseId}/attachments`,
      payload,
    );
  }

  public addNoteToTestCases(testSeriesId: number, payload: TestRequestRaw.Schemas.AddNotes): Promise<AxiosResponse> {
    return this.api.post(`/test-request/test-case-notes/${testSeriesId}`, payload);
  }

  public updateNoteToTestCases(
    testSeriesId: number,
    testCaseId: number,
    payload: TestRequestRaw.Schemas.UpdateTestCaseNotes,
  ): Promise<AxiosResponse> {
    return this.api.put(`/test-request/test-case-notes/${testSeriesId}/${testCaseId}`, payload);
  }

  public getTestCaseNotes(
    testSeriesId: number,
    testCaseId: number,
  ): Promise<AxiosResponse<TestRequestRaw.Schemas.TestCaseNoteListResponse[]>> {
    return this.api.get(`/test-request/test-case-notes/${testSeriesId}/${testCaseId}`);
  }

  public fetchRegainedSubstrates(
    testSeriesId: number,
    substrateSectionId: number,
  ): Promise<
    AxiosResponse<
      {
        substrateId: number;
        originTestCaseId: number;
        actionFlow: { actionValues: []; name: string };
      }[]
    >
  > {
    return this.api.get(
      `/test-request/test-series/${testSeriesId}/substrate-section/${substrateSectionId}/regained-substrates`,
    );
  }
}

export default TestSeriesApi;
